import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7071/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth API
export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  updateProfile: async (payload) => {
    const response = await api.post('/auth/update-profile', payload);
    return response.data;
  },
  requestDependents: async (data) => {
    const response = await api.post('/auth/request-dependents', data);
    return response.data;
  },
  getDependents: async () => {
    const response = await api.get('/auth/dependents');
    return response.data;
  },
  getDependentRequests: async () => {
    const response = await api.get('/auth/dependent-requests');
    return response.data;
  },
  switchAccount: async (targetUserId) => {
    const response = await api.post('/auth/switch-account', { targetUserId });
    return response.data;
  },
};

// Races API
export const racesAPI = {
  getRaces: async (params = {}) => {
    const response = await api.get('/races', { params });
    return response.data;
  },
  getRaceById: async (raceId) => {
    const response = await api.get(`/races/${raceId}`);
    return response.data;
  },
  getRaceOdds: async (raceId) => {
    const response = await api.get(`/races/${raceId}/odds`);
    return response.data;
  },
  createRace: async (raceData) => {
    const response = await api.post('/races', raceData);
    return response.data;
  },
  updateRace: async (raceId, raceData) => {
    const response = await api.put(`/races/${raceId}`, raceData);
    return response.data;
  },
  deleteRace: async (raceId) => {
    const response = await api.delete(`/races/${raceId}`);
    return response.data;
  },
  updateHorses: async (raceId, horsesData) => {
    const response = await api.post(`/races/${raceId}/horses`, horsesData);
    return response.data;
  },
  submitRaceResults: async (raceId, resultsData) => {
    const response = await api.post(`/races/${raceId}/results`, resultsData);
    return response.data;
  },
  exportRaces: async (statusFilter = null) => {
    const params = statusFilter ? { status: statusFilter } : {};
    const response = await api.get('/races/export', { params });
    return response.data;
  },
  importRaces: async (races) => {
    const payload = Array.isArray(races) ? { races } : races;
    const response = await api.post('/races/import', payload);
    return response.data;
  },
};

// Picks API
export const picksAPI = {
  createPick: async (pickData) => {
    const response = await api.post('/picks', pickData);
    return response.data;
  },
  getMyPicks: async (params = {}) => {
    const response = await api.get('/picks/my-picks', { params });
    return response.data;
  },
  getUserPicks: async (userId, params = {}) => {
    const response = await api.get(`/picks/user/${userId}`, { params });
    return response.data;
  },
  getRacePicks: async (raceId) => {
    const response = await api.get(`/picks/race/${raceId}`);
    return response.data;
  },
  updatePick: async (pickId, pickData) => {
    const response = await api.put(`/picks/${pickId}`, pickData);
    return response.data;
  },
  deletePick: async (pickId) => {
    const response = await api.delete(`/picks/${pickId}`);
    return response.data;
  },
};

// Leaderboard API
export const leaderboardAPI = {
  getLeaderboard: async (params = {}) => {
    const response = await api.get('/leaderboard', { params });
    return response.data;
  },
  getUserStats: async (userId) => {
    const response = await api.get(`/leaderboard/${userId}`);
    return response.data;
  },
};

// Health Check
export const healthAPI = {
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Management API
export const managementAPI = {
  updateUserPermissions: async (userId, permissions) => {
    const response = await api.post(`/management/update-user-permissions/${userId}`, { permissions });
    return response.data;
  },
  reassignDependent: async (dependentUserId, newParentUserId) => {
    const response = await api.post(`/management/reassign-dependent/${dependentUserId}`, { newParentUserId });
    return response.data;
  },
  convertDependentToIndependent: async (dependentUserId, newPassword, newEmail) => {
    const response = await api.post(`/management/convert-dependent-to-independent/${dependentUserId}`, { newPassword, newEmail });
    return response.data;
  },
  getDependentRequestsStats: async () => {
    const response = await api.get('/management/dependent-requests-stats');
    return response.data;
  },
  createUser: async (userData) => {
    const response = await api.post('/management/create-user', userData);
    return response.data;
  },
  getUserPicks: async (userId, params = {}) => {
    const response = await api.get(`/management/user-picks/${userId}`, { params });
    return response.data;
  },
  updateUserPick: async (pickId, pickData) => {
    const response = await api.put(`/management/update-user-pick/${pickId}`, pickData);
    return response.data;
  },
  createUserPick: async (pickData) => {
    const response = await api.post('/management/create-user-pick', pickData);
    return response.data;
  },
};

// Tidbits API
export const tidbitsAPI = {
  getAllTidbits: async (params = {}) => {
    const response = await api.get('/tidbits', { params });
    return response.data;
  },
  getTidbit: async (tidbitId) => {
    const response = await api.get(`/tidbits/${tidbitId}`);
    return response.data;
  },
  getRaceTidbits: async (raceId, horseId = null) => {
    const url = horseId 
      ? `/tidbits/race/${raceId}/${horseId}`
      : `/tidbits/race/${raceId}`;
    const response = await api.get(url);
    return response.data;
  },
  createTidbit: async (tidbitData) => {
    const response = await api.post('/tidbits', tidbitData);
    return response.data;
  },
  updateTidbit: async (tidbitId, tidbitData) => {
    const response = await api.put(`/tidbits/${tidbitId}`, tidbitData);
    return response.data;
  },
  deleteTidbit: async (tidbitId) => {
    const response = await api.delete(`/tidbits/${tidbitId}`);
    return response.data;
  },
};

// Horse Racing API (RapidAPI integration - UK & Ireland)
export const horseRacingAPI = {
  getRacecards: async (date) => {
    const params = date ? { date } : {};
    const response = await api.get('/horse-racing/racecards', { params });
    return response.data;
  },
  getResults: async (date) => {
    const params = date ? { date } : {};
    const response = await api.get('/horse-racing/results', { params });
    return response.data;
  },
  getRaceDetail: async (id) => {
    const response = await api.get('/horse-racing/race', { params: { id } });
    return response.data;
  },
  getSyncStatus: async () => {
    const response = await api.get('/horse-racing/sync-status');
    return response.data;
  },
  syncToDatabase: async (date, force = false) => {
    const response = await api.post('/horse-racing/sync', { date, force });
    return response.data;
  },
  importRace: async (id) => {
    const response = await api.post('/horse-racing/import', { id });
    return response.data;
  },
};

// Notifications API
export const notificationsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },
  markRead: async (notificationId) => {
    const response = await api.post(`/notifications/${notificationId}/read`);
    return response.data;
  },
  remove: async (notificationId) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },
  dismissAll: async () => {
    const response = await api.post('/notifications/dismiss-all');
    return response.data;
  },
  replyToEmail: async (notificationId, { subject, body }) => {
    const response = await api.post(`/notifications/${notificationId}/reply`, { subject, body });
    return response.data;
  },
};

// Email compose / broadcast API
export const emailAPI = {
  getUsers: async () => {
    const response = await api.get('/email/users');
    return response.data;
  },
  send: async (payload) => {
    const response = await api.post('/email/send', payload);
    return response.data;
  },
};

// Chat API
export const chatAPI = {
  getGeneralMessages: async (params = {}) => {
    const response = await api.get('/chat', { params });
    return response.data;
  },
  getRaceMessages: async (raceId, params = {}) => {
    const response = await api.get(`/chat/race/${raceId}`, { params });
    return response.data;
  },
  postMessage: async (messageData) => {
    const response = await api.post('/chat', messageData);
    return response.data;
  },
  deleteMessage: async (messageId) => {
    const response = await api.delete(`/chat/${messageId}`);
    return response.data;
  },
};

// Admin API for dependent accounts
export const dependentRequestsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/management/dependent-requests', { params });
    return response.data;
  },
  approve: async (requestId, notes = '') => {
    const response = await api.post(`/management/approve-dependent-request/${requestId}`, { notes });
    return response.data;
  },
  reject: async (requestId, reason = '') => {
    const response = await api.post(`/management/reject-dependent-request/${requestId}`, { reason });
    return response.data;
  },
};

// Push Notifications API
export const pushAPI = {
  sendTestNotification: async () => {
    const response = await api.post('/push/test');
    return response.data;
  },
};

export default api;


